import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  Typography,
  Toolbar,
  Stack,
  IconButton,
  Paper,
  Grid,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router';

import { fetchRigs, clearRigs } from '../../../actions/rigs';
import { fetchActiveRegion } from '../../../actions/regions';

import Nodes from './Nodes';
import Datastores from './Datastores';
import { fetchDatastores, clearDatastores } from '../../../actions/datastores';
import { fetchServices, clearServices } from '../../../actions/services';
import Services from './Services';
import {
  fetchDeviceConfigs,
  clearDeviceConfigs,
} from '../../../actions/deviceConfigs';
import { clearErrorMessage } from '../../../actions/errorMessage';
import { fetchActiveProvider } from '../../../actions/providers';
import LoadingMask from '../../shared/LoadingMask';
import {
  fetchDataConnectors,
  clearDataConnectors,
} from '../../../actions/dataConnectors';
import DataConnectors from './DataConnectors';
import ErrorSnackBar from '../../shared/ErrorSnackBar';
import Oops from '@/components/shared/Oops';
import { getResourcesDashboardPath } from '@/util/routePaths';

export default function RegionDashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { provider_uuid, region_uuid } = useParams();

  const api_error = useSelector((state) => state.errorMessage);
  const provider = useSelector((state) => state.provider);
  const region = useSelector((state) => state.region);
  const rigs = useSelector((state) => state.rigs);
  const region_key_ref = useRef(null);
  const last_fetched_key_ref = useRef(null);
  const failed_region_key_ref = useRef(null);

  const [loading, setLoading] = useState(true);
  const [load_error, setLoadError] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(clearErrorMessage());
    };
  }, [dispatch]);

  useEffect(() => {
    const current_region_key = `${provider_uuid}:${region_uuid}`;
    if (failed_region_key_ref.current === current_region_key) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        if (!provider) {
          await dispatch(fetchActiveProvider(provider_uuid));
          return;
        }
        if (!region || region.region_uuid !== region_uuid) {
          await dispatch(fetchActiveRegion(provider_uuid, region_uuid));
        }
      } catch {
        if (cancelled) {
          return;
        }
        failed_region_key_ref.current = current_region_key;
        setLoadError(true);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, provider, region, provider_uuid, region_uuid]);

  useEffect(() => {
    const next_region_key = `${provider_uuid}:${region_uuid}`;

    if (region_key_ref.current !== next_region_key) {
      region_key_ref.current = next_region_key;
      last_fetched_key_ref.current = null;
      failed_region_key_ref.current = null;
      setLoadError(false);
      setLoading(true);
      dispatch(clearDatastores());
      dispatch(clearDataConnectors());
      dispatch(clearServices());
      dispatch(clearDeviceConfigs());
      dispatch(clearRigs());
    }
  }, [dispatch, provider_uuid, region_uuid]);

  useEffect(() => {
    (async () => {
      const current_region_key = `${provider_uuid}:${region_uuid}`;
      if (!region || region.region_uuid !== region_uuid) {
        return;
      }

      if (last_fetched_key_ref.current === current_region_key) {
        return;
      }

      last_fetched_key_ref.current = current_region_key;

      dispatch(fetchDatastores(region.provider_uuid, region.region_uuid));
      dispatch(fetchDataConnectors(region.provider_uuid, region.region_uuid));
      dispatch(fetchServices(region.provider_uuid, region.region_uuid));
      dispatch(fetchDeviceConfigs(region.provider_uuid, region.region_uuid));
      await dispatch(fetchRigs(region.provider_uuid, region.region_uuid));
      setLoading(false);
    })();
  }, [dispatch, provider_uuid, region_uuid, region]);

  if (loading) {
    return <LoadingMask />;
  }

  if (load_error) {
    return <Oops />;
  }

  return (
    <Container>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 12, lg: 12 }}>
              <Paper sx={{ display: 'flex', width: '100%' }} elevation={2}>
                <Toolbar>
                  <Stack spacing={2} direction='row' alignItems='center'>
                    <IconButton
                      onClick={() => navigate(getResourcesDashboardPath())}
                      disabled={loading}
                    >
                      <ArrowBack />
                    </IconButton>
                    <Typography
                      variant='h4'
                      paragraph
                      gutterBottom
                      align='center'
                    >
                      Region {region.name}
                    </Typography>
                  </Stack>
                </Toolbar>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 12, lg: 12 }}>
              <Nodes rigs={rigs} region={region} />
              <Datastores region={region} autoFetch={false} />
              <DataConnectors region={region} autoFetch={false} />
              <Services region={region} autoFetch={false} />
            </Grid>
          </Grid>
          <ErrorSnackBar message={api_error} />
        </Container>
  );
}
